import { Component, EventEmitter, Output } from '@angular/core';
import { AuthorizationAwareComponent } from '../../../core/authorization-aware-component';
import { AppState } from '../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { DetailState,  selectmarketAreaDetailState, selectstoreDetailState, selectvolumeDetailState } from '../../detail-state';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { Store } from '../../../core/models';
import { SalesVolumeDisplayType } from '../../../core/user-preferences/sales-volume-display-type.enum';
import { SalesSqftDisplayType } from '../../../core/user-preferences/sales-sqft-display-type.enum';
import { formatAsCurrency } from '../../../core/util/string-utils';
import { DefinitionService } from '../../../core/definition/definition.service';
import { Definition } from '../../../core/definition/definition';
import {
  ActiveStoreStatuses,
  FutureStoreStatuses,
  HistoricalStoreStatuses,
  StoreStatusType
} from '../../../core/identity/constant/store-status-type.enum';
import * as _ from 'lodash';
import { WizardRunnerService } from '../../../core/wizard/wizard-runner.service';
import { SalesAreaWizard } from './sales-area-wizard/sales-area-wizard';
import { SalesVolumeHistoryWizard } from './sales-volume-history-wizard/sales-volume-history-wizard';
import { StatusHistoryWizard } from './status-history-wizard/status-history-wizard';
import { StoreVolume } from '../../../core/store/volume/store-volume';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { DetailLoadingService } from 'src/app/core/store/detail-loading.service';

@Component({
  standalone: false,
  selector: 'mtn-store-critical-insights-panel',
  templateUrl: './store-critical-insights-panel.component.html',
  styleUrls: ['./store-critical-insights-panel.component.scss']
})
export class StoreCriticalInsightsPanelComponent extends AuthorizationAwareComponent {

  @Output() uiReady = new EventEmitter<void>();

  cardDescriptionArea: string;
  cardDescriptionSalesSqft: string;
  cardDescriptionStatus: string;
  cardDescriptionVolume: string;
  cardTitleArea: string;
  cardTitleSalesSqft: string;
  cardTitleStatus: string;
  cardTitleVolume: string;
  isVolumeHighConfidence = false;
  maxHighlightCount = 4;
  marketName: any;
  isLoading = true;
  statusColor: string;
  store: Store;
  volume: StoreVolume;
  volumeDate: string;

  private _isStoreCardsInitialized = false;
  // private _isVolumeCardsInitialized = false;

  constructor(private definitionService: DefinitionService,
    private hitTrackingService: HitTrackingService,
    protected ngrxStore: NgrxStore<AppState>,
    private detailLoadingService: DetailLoadingService,
    private wizardRunner: WizardRunnerService) {
    super(ngrxStore);
  }

  onAuthorizationChange(): void {
  }

  onAuthorizationInit(): void {
    this.loadStore();
     this.addSubscription(
    this.detailLoadingService.isLoading$.subscribe((loading: boolean) => {
      this.isLoading = loading;
      this.initNotAvailable();
      // if (!loading) {
      //   setTimeout(() => this.uiReady.emit(), 500);
      // }
    })
  );
  }

  openSalesVolumeHistoryWizard(actionDescription: string = 'Sales Volume Card'): void {
    this.trackCardClick(actionDescription, { volume: this.volume });

    const wizard = new SalesVolumeHistoryWizard();
    wizard.model = {
      store: this.store,
      userPreferences: this.userPreferences
    };

    this.wizardRunner.run(wizard);
  }


  openSalesAreaWizard(): void {
    this.trackCardClick('Sales Area Card');
    const wizard = new SalesAreaWizard();
    wizard.model = {
      store: this.store
    };

    this.wizardRunner.run(wizard);
  }

  openStatusHistoryWizard(): void {
    this.trackCardClick('Store Status Card', {
      status: this.store?.getCurrentStatus()?.getStatusSystemName()
    });
    const wizard = new StatusHistoryWizard();
    wizard.model = {
      store: this.store
    };

    this.wizardRunner.run(wizard);
  }

  toggleMaxCount(): void {
    const newState = this.maxHighlightCount === 4 ? 'VIEW_ALL' : 'VIEW_LESS';
    this.trackCardClick('Toggle Highlight Count', { viewState: newState });

    this.maxHighlightCount = this.maxHighlightCount === 4 ? 100 : 4;
  }


  private initAreaCard(): void {
    this.cardDescriptionArea = 'Sales / Total Area';
    this.cardTitleArea = 'Unknown';
    if (this.store.grocerySalesArea && this.store.totalArea) {
      this.cardTitleArea = `${this.store.grocerySalesArea.toLocaleString()} / ${this.store.totalArea.toLocaleString()} sqft`;
    }
  }

  private initSalesSqftCard(): void {
    if (this.volume) {
      const isVolumeAvailable = !this.volume && !this.volume.total; /*Available means MTN has a volume, but the value isn't purchased*/
      const isVolumePurchased = this.volume?.total;

      if (isVolumePurchased) {
        this.cardTitleSalesSqft = `${formatAsCurrency(this.volume.salesSqft)} /sqft`;
      } else if (isVolumeAvailable) {
        this.cardTitleSalesSqft = 'Not Purchased';
      } else {
        this.cardTitleSalesSqft = 'Unknown';
      }
    } else {
      this.cardTitleSalesSqft = 'Not Available';
    }
    this.cardDescriptionSalesSqft = 'Est. Sales/SQFT (';
    this.cardDescriptionSalesSqft += this.userPreferences.salesVolumeDisplayMode === SalesVolumeDisplayType.ANNUAL ? 'Annually' : 'Weekly';
    this.cardDescriptionSalesSqft += ', ';
    this.cardDescriptionSalesSqft += this.userPreferences.salesSqftDisplayMode === SalesSqftDisplayType.SALES_AREA ? 'Sales Area' : 'Total Area';
    this.cardDescriptionSalesSqft += ')';
  }

  private initStatusCard(): void {
    this.cardDescriptionStatus = 'Store Status';
    const status = this.store.getCurrentStatus();
    if (status) {

      this.definitionService.findOneStoreStatusType(status.getStatusSystemName())
        .pipe(take(1))
        .subscribe((definition: Definition<StoreStatusType>) => {
          this.cardTitleStatus = definition.displayName;
        });
    } else {
      this.cardTitleStatus = 'Unknown';
    }

    if (_.includes(FutureStoreStatuses, status.getStatusSystemName())) {
      this.statusColor = 'dodgerblue';
    } else if (_.includes(ActiveStoreStatuses, status.getStatusSystemName())) {
      this.statusColor = 'limegreen';
    } else if (_.includes(HistoricalStoreStatuses, status.getStatusSystemName())) {
      this.statusColor = 'red';
    }
  }

  private initVolumeCard(): void {
    if (this.volume) {
      const isVolumeAvailable = this.volume && !this.volume.total; /*Available means MTN has a volume, but the value isn't purchased*/
      const isVolumePurchased = this.volume?.total;

      if (isVolumePurchased) {
        this.cardTitleVolume = formatAsCurrency(this.volume.total, false);
      } else if (isVolumeAvailable) {
        this.cardTitleVolume = 'Not Purchased';
      } else {
        this.cardTitleVolume = 'Unknown';
      }
      this.getDate();
    } else {
      this.cardTitleVolume = 'Not Available';
    }
    this.cardDescriptionVolume = 'Est. Sales Volume';
    this.cardDescriptionVolume += ' (';
    this.cardDescriptionVolume += this.userPreferences.salesVolumeDisplayMode === SalesVolumeDisplayType.ANNUAL ? 'Annually' : 'Weekly';
    this.cardDescriptionVolume += ')';
    if (this.volumeDate) {
      this.cardDescriptionVolume += ' ';
      this.cardDescriptionVolume += this.volumeDate;
    }
  }

  getDate() {
    const volumeDate = this.volume.date;
    const newVolumeDate = new Date(volumeDate);
    const month = (newVolumeDate.getMonth() + 1).toString().padStart(2, '0');
    const year = newVolumeDate.getFullYear();

    const formattedDate = `${month}/${year}`;
    this.volumeDate = formattedDate;
  }

  private initStoreCards(): void {
    if (this.store && !this._isStoreCardsInitialized) {
      this._isStoreCardsInitialized = true;
      this.initAreaCard();
      this.initStatusCard();
    }
  }

  private initVolumeCards(): void {
    // if (!this._isVolumeCardsInitialized) {
    //   this._isVolumeCardsInitialized = true;
    //   console.log("--------------------");
    //   console.log(this.volume);
      this.initVolumeCard();
      this.initSalesSqftCard();
    // }
  }

  private loadStore(): void {
  // --- STORE ---
  this.addSubscription(
    selectstoreDetailState(this.ngrxStore)
      .pipe(
        filter((store) => !!store),
        distinctUntilChanged((prev, curr) => prev === curr)
      )
      .subscribe((store) => {
        this.store = store;
        this.initStoreCards();
      })
  );

  // --- VOLUME ---
  this.addSubscription(
    selectvolumeDetailState(this.ngrxStore)
      .pipe(
        // filter((volume) => !!volume),
        distinctUntilChanged((prev, curr) => prev === curr)
      )
      .subscribe((volume) => {
        this.volume = volume;
        if(volume)
        {
          this.initVolumeCards();
        }
        this.initNotAvailable();
      })
  );



  // --- MARKET AREA ---
  this.addSubscription(
    selectmarketAreaDetailState(this.ngrxStore)
      .pipe(
        map((state: DetailState) => state.marketArea),
        filter((marketArea) => !!marketArea),
        distinctUntilChanged((prev, curr) => prev === curr)
      )
      .subscribe((marketArea) => {
        this.marketName = marketArea.getFeature().properties['name'];
      })
  );
}

  private initNotAvailable(): void {
     if(!this.isLoading && !this.volume)
  {
     this.cardTitleVolume = 'Not Available';
     this.cardTitleSalesSqft = 'Not Available';

     this.cardDescriptionVolume = 'Est. Sales Volume';
    this.cardDescriptionVolume += ' (';
    this.cardDescriptionVolume += this.userPreferences.salesVolumeDisplayMode === SalesVolumeDisplayType.ANNUAL ? 'Annually' : 'Weekly';
    this.cardDescriptionVolume += ')';

    this.cardDescriptionSalesSqft = 'Est. Sales/SQFT (';
    this.cardDescriptionSalesSqft += this.userPreferences.salesVolumeDisplayMode === SalesVolumeDisplayType.ANNUAL ? 'Annually' : 'Weekly';
    this.cardDescriptionSalesSqft += ', ';
    this.cardDescriptionSalesSqft += this.userPreferences.salesSqftDisplayMode === SalesSqftDisplayType.SALES_AREA ? 'Sales Area' : 'Total Area';
    this.cardDescriptionSalesSqft += ')';

  }
  }



  private trackCardClick(actionDescription: string, returnedData: any = {}): void {
    const location = this.store?.space?.location;
    const shoppingCenter = this.store?.space?.shoppingCenter;
    console.log(returnedData);
    const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      addressLine1: location?.addressLine1,
      city: location?.city,
      cbsa: this.marketName,
      state: location?.state,
      postalCode: location?.postalCode,
      county: location?.county,
      shoppingCenterName: shoppingCenter?.name,
      landlord: shoppingCenter?.ownerCompany?.name
    };

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.STORE_DETAILS_CRITICAL_INSIGHTS,
      metadata,
      USER_TRACKING_ACTIONS.CLICK_CRITICAL_INSIGHT,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.CLICK_CRITICAL_INSIGHT](actionDescription)
    ).subscribe();
  }

}
